import { XCircleIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import { useId } from 'react';

import { Button } from '@/components/common/button';

import { COORDINATE_HELPER_TEXT, type CoordinateAxis } from '../coordinates';
import type { CircleErrors } from '../use-circle-draft';

import styles from './coordinate-rows.module.scss';

export type CircleSectionProps = {
  lat: string;
  lon: string;
  /** The **Radius** as typed, in feet and without its unit — the "Ft" below is a label. */
  radius: string;
  /** An absent key means that cell is not in error. */
  errors: CircleErrors;
  canSave: boolean;
  onValueChange: (axis: CoordinateAxis, raw: string) => void;
  onRadiusChange: (raw: string) => void;
  onCellBlur: (axis: CoordinateAxis) => void;
  onRadiusBlur: () => void;
  onCancel: () => void;
  onSave: () => void;
};

/** One labelled input with its own inline error. Module-local: three of these appear here and
 *  nowhere else, so it is deduplication rather than a component the library needs. */
type FieldProps = {
  label: string;
  value: string;
  error?: string;
  placeholder?: string;
  suffix?: string;
  clearable?: boolean;
  onChange: (raw: string) => void;
  onBlur: () => void;
};

function Field({
  label,
  value,
  error,
  placeholder,
  suffix,
  clearable = false,
  onChange,
  onBlur,
}: FieldProps) {
  const errorId = useId();

  return (
    <div className={styles.cell}>
      <Input
        aria-label={label}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        status={error ? 'error' : undefined}
        placeholder={placeholder}
        value={value}
        // antd renders `allowClear` only when the input is populated, which IS the criterion's
        // clear (×). The Radius has none: Figma shows only "Ft" there, and no criterion asks for one.
        allowClear={clearable ? { clearIcon: <XCircleIcon size={16} /> } : false}
        suffix={suffix ? <span className={styles.suffix}>{suffix}</span> : undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The Field Mapping panel's **Co-ordinates** section in circle **Shape mode** (Figma
 * `598:540023`–`598:540042`): a **Mid Point** Lat/Long pair and a **Radius** in feet.
 *
 * Its polygon sibling `<CoordinateRows>`' second half, and it **shares that component's
 * stylesheet** rather than taking a copy — the same one-stylesheet-per-family arrangement
 * `map-search.module.scss` and `dialog.module.scss` already use. The two modes are one panel
 * section in two layouts, and drift between their chrome would be visible on a single tool click.
 *
 * Three differences from the polygon layout, all of them Figma's:
 *
 * - **no "Add point" chip** — the heading frame `598:540025` holds only the heading;
 * - **no trash control** on either group (`598:540036` / `598:540042` are both hidden) — a circle
 *   is one shape, and its Mid Point is deleted from the map, not from a row;
 * - **the Radius carries a "Ft" suffix**, rendered outside the control, which is what makes "never
 *   editable and never part of the value" structural rather than a rule something strips.
 *
 * The groups container reuses `.rows`: its `flex: 1` is what keeps the foot pinned at the panel's
 * bottom (Figma puts the foot at y=527 of a 559 body), and its `overflow-y` never triggers at two
 * groups.
 *
 * `Save` is `aria-disabled`, never `disabled`: pressing it while a cell is blank has to flag the
 * blanks, and a disabled control dispatches no click. The same call `<CoordinateRows>` and
 * `Save Mapping` already make.
 */
export function CircleSection({
  lat,
  lon,
  radius,
  errors,
  canSave,
  onValueChange,
  onRadiusChange,
  onCellBlur,
  onRadiusBlur,
  onCancel,
  onSave,
}: CircleSectionProps) {
  const headingId = useId();

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.header}>
        <p id={headingId} className={styles.heading}>
          Co-ordinates
        </p>
        <p className={styles.helper}>{COORDINATE_HELPER_TEXT}</p>
      </div>

      <div className={styles.rows}>
        <div className={styles.row}>
          <p className={styles.groupLabel}>Mid Point</p>
          <div className={styles.inputs}>
            <Field
              label="Mid point latitude"
              value={lat}
              error={errors.lat}
              placeholder="Lat"
              clearable
              onChange={(raw) => onValueChange('lat', raw)}
              onBlur={() => onCellBlur('lat')}
            />
            <Field
              label="Mid point longitude"
              value={lon}
              error={errors.lon}
              placeholder="Long"
              clearable
              onChange={(raw) => onValueChange('lon', raw)}
              onBlur={() => onCellBlur('lon')}
            />
          </div>
        </div>

        <div className={styles.row}>
          <p className={styles.groupLabel}>Radius</p>
          <div className={styles.inputs}>
            <Field
              label="Radius in feet"
              value={radius}
              error={errors.radius}
              placeholder="Enter in feet"
              suffix="Ft"
              onChange={onRadiusChange}
              onBlur={onRadiusBlur}
            />
          </div>
        </div>
      </div>

      <div className={styles.foot}>
        <Button variant="outline" size="small" className={styles.action} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="neutral"
          size="small"
          className={styles.action}
          aria-disabled={!canSave}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    </section>
  );
}
